import { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const COLS = [
  ["glossy", "Veloskin Glossy"],
  ["matte", "Veloskin Matte"],
  ["x250", "Veloskin X250"],
];

const ListEditor = ({ kind, title, tint, canWrite, tiers, onReload }) => {
  const [rows, setRows] = useState(tiers);
  useEffect(() => setRows(tiers), [tiers]);

  const setCell = (i, key, val) =>
    setRows((r) => r.map((t, idx) => (idx === i ? { ...t, [key]: val } : t)));
  const addRow = () => setRows((r) => [...r, { sqft: "", glossy: "", matte: "", x250: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      const clean = rows
        .filter((t) => t.sqft !== "" && t.sqft != null)
        .map((t) => ({
          sqft: Number(t.sqft),
          glossy: Number(t.glossy) || 0,
          matte: Number(t.matte) || 0,
          x250: Number(t.x250) || 0,
        }));
      const { data } = await api.put(`/pricing/price-lists/${kind}`, { tiers: clean });
      setRows(data.tiers);
      toast.success(`${title} saved — applies to new jobs & quotes only`);
      onReload && onReload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-brand-line shadow-none" data-testid={`price-list-${kind}`}>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${tint}`} /> {title}
        </CardTitle>
        {canWrite && (
          <Button size="sm" onClick={save} data-testid={`save-price-list-${kind}`}>
            <Save className="mr-2 h-4 w-4" /> Save
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-2">SQ FT</th>
                {COLS.map(([k, label]) => (
                  <th key={k} className="py-2 pr-2">{label}</th>
                ))}
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`price-row-${kind}-${i}`}>
                  <td className="py-1.5 pr-2">
                    <Input
                      className="h-9 w-20"
                      type="number"
                      value={t.sqft}
                      disabled={!canWrite}
                      data-testid={`price-${kind}-sqft-${i}`}
                      onChange={(e) => setCell(i, "sqft", e.target.value)}
                    />
                  </td>
                  {COLS.map(([k]) => (
                    <td key={k} className="py-1.5 pr-2">
                      <Input
                        className="h-9 w-28"
                        type="number"
                        value={t[k]}
                        disabled={!canWrite}
                        data-testid={`price-${kind}-${k}-${i}`}
                        onChange={(e) => setCell(i, k, e.target.value)}
                      />
                    </td>
                  ))}
                  {canWrite && (
                    <td className="py-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        data-testid={`price-remove-${kind}-${i}`}
                        onClick={() => removeRow(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            data-testid={`add-price-row-${kind}`}
            onClick={addRow}
          >
            <Plus className="mr-2 h-3.5 w-3.5" /> Add sqft tier
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export const PriceLists = ({ canWrite }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/pricing/price-lists").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="price-lists-panel">
      <p className="text-sm text-slate-500">
        PPF price by sqft tier and film. Brands marked <strong>Premium</strong> use the Premium list;
        all others use the Standard list. If a car's sqft falls between tiers, the nearest upper tier
        is used (e.g. 182 or 189 sqft → the 190 tier). Edits apply to <strong>new</strong> jobs &amp; quotes only — existing records are unchanged.
      </p>
      <ListEditor kind="standard" title="Standard price list" tint="bg-slate-400"
        canWrite={canWrite} tiers={data.standard} onReload={load} />
      <ListEditor kind="premium" title="Premium price list" tint="bg-amber-500"
        canWrite={canWrite} tiers={data.premium} onReload={load} />
    </div>
  );
};

export default PriceLists;
